/**
 * Magic World — comportamiento del carrito desplegable.
 *
 * Abre el cajón en vez de navegar a /cart en tres casos:
 *   1. el botón de carrito de la cabecera,
 *   2. cualquier formulario de "agregar al carrito",
 *   3. los enlaces permanentes /cart/<variante>:<cantidad>.
 *
 * Los textos salen de locales/en.default.json (ya traducido), así que aquí
 * solo viven las cadenas que no existen en el tema base.
 */
(() => {
  const UMBRAL_ENVIO_GRATIS = Number(document.documentElement.dataset.envioGratis || 30000);

  const dinero = (centavos) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'MXN',
      minimumFractionDigits: 2,
    }).format(centavos / 100);

  const cajon = () => document.querySelector('cart-drawer');
  const rutaCarrito = () => (window.routes && window.routes.cart_url) || '/cart';

  /* ---------- barra de envío gratis ---------- */

  const pintarProgreso = (carrito) => {
    const raiz = cajon();
    const cabecera = raiz && raiz.querySelector('.drawer__header');
    if (!raiz || !cabecera) return;

    let barra = raiz.querySelector('.magic-cart-progress');
    if (!barra) {
      barra = document.createElement('div');
      barra.className = 'magic-cart-progress';
      cabecera.insertAdjacentElement('afterend', barra);
    }

    if (!carrito.item_count) {
      barra.hidden = true;
      return;
    }
    barra.hidden = false;

    const falta = Math.max(UMBRAL_ENVIO_GRATIS - carrito.total_price, 0);
    const porcentaje = Math.min((carrito.total_price / UMBRAL_ENVIO_GRATIS) * 100, 100);
    const logrado = falta === 0;
    const mensaje = logrado
      ? '¡Listo! Tu envío es gratis'
      : `Te faltan ${dinero(falta)} para el envío gratis`;

    barra.classList.toggle('magic-cart-progress--logrado', logrado);
    barra.innerHTML = `
      <p class="magic-cart-progress__copy">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 6.5h12v10h-12zM14.5 10h4l2.5 3v3.5h-6.5"/><circle cx="7" cy="17.5" r="1.7"/><circle cx="17.5" cy="17.5" r="1.7"/></svg>
        ${mensaje}
      </p>
      <div class="magic-cart-progress__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(porcentaje)}">
        <span style="width:${porcentaje}%"></span>
      </div>`;
  };

  /* Dawn solo quita is-empty del div interno; la raíz conserva la clase y
     eso esconde la cabecera y colapsa la lista tras agregar por AJAX. */
  const sincronizarVacio = (carrito) => {
    const raiz = cajon();
    if (!raiz) return;
    raiz.classList.toggle('is-empty', carrito.item_count === 0);
    const interno = raiz.querySelector('.drawer__inner');
    if (interno) interno.classList.toggle('is-empty', carrito.item_count === 0);
  };

  const refrescar = async () => {
    if (!cajon()) return;
    try {
      const r = await fetch(`${rutaCarrito()}.js`, { credentials: 'same-origin' });
      if (!r.ok) return;
      const carrito = await r.json();
      sincronizarVacio(carrito);
      pintarProgreso(carrito);
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: carrito }));
    } catch (_) {
      /* la barra es un extra: si falla, el cajón sigue funcionando */
    }
  };

  /* ---------- agregar y abrir ---------- */

  const avisarError = (disparador, texto) => {
    if (!disparador) return;
    let aviso = disparador.parentElement && disparador.parentElement.querySelector('.magic-cart-error');
    if (!aviso) {
      aviso = document.createElement('p');
      aviso.className = 'magic-cart-error';
      aviso.setAttribute('role', 'alert');
      disparador.insertAdjacentElement('afterend', aviso);
    }
    aviso.textContent = texto;
  };

  const agregar = async (cuerpo, disparador) => {
    const raiz = cajon();
    if (!raiz) return false;

    if (disparador) disparador.setAttribute('aria-busy', 'true');
    try {
      const r = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: cuerpo,
      });
      const estado = await r.json();
      if (!r.ok) throw new Error(estado.description || estado.message || 'No se pudo agregar el producto');

      if (disparador) raiz.setActiveElement(disparador);
      raiz.classList.remove('is-empty');
      raiz.renderContents(estado);
      setTimeout(refrescar, 90);
      return true;
    } catch (error) {
      avisarError(disparador, error.message);
      return false;
    } finally {
      if (disparador) disparador.removeAttribute('aria-busy');
    }
  };

  const conSecciones = (datos) => {
    datos.append('sections', 'cart-drawer,cart-icon-bubble');
    datos.append('sections_url', window.location.pathname);
    return datos;
  };

  /* ---------- 1. botón de la cabecera ---------- */

  document.addEventListener('click', (e) => {
    const boton = e.target.closest('.cabecera__carrito, [data-abre-carrito]');
    if (!boton || !cajon()) return;
    e.preventDefault();
    cajon().open(boton);
    refrescar();
  });

  /* ---------- 2. formularios de compra ---------- */

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form || !cajon() || e.defaultPrevented) return;
    e.preventDefault();
    const boton = form.querySelector('[type="submit"], [name="add"]');
    agregar(conSecciones(new FormData(form)), boton);
  });

  /* ---------- 3. enlaces /cart/<variante>:<cantidad> ---------- */

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const enlace = e.target.closest('a[href*="/cart/"]');
    if (!enlace || !cajon()) return;

    const url = new URL(enlace.href, window.location.origin);
    // "Comprar ahora" va directo al checkout: no lo interceptamos
    if (url.searchParams.has('checkout')) return;

    const m = url.pathname.match(/\/cart\/(\d+)(?::(\d+))?$/);
    if (!m) return;

    e.preventDefault();
    const datos = new FormData();
    datos.append('id', m[1]);
    datos.append('quantity', m[2] || 1);
    agregar(conSecciones(datos), enlace);
  });

  /* ---------- mantener la barra al día ---------- */

  document.addEventListener('DOMContentLoaded', () => {
    const raiz = cajon();
    if (!raiz) return;
    refrescar();
    new MutationObserver(() => {
      if (raiz.classList.contains('active')) requestAnimationFrame(() => pintarPendiente());
    }).observe(raiz, { attributes: true, attributeFilter: ['class'] });
    // los cambios de cantidad dentro del cajón vuelven a renderizar la lista
    raiz.addEventListener('click', (e) => {
      if (e.target.closest('cart-remove-button, .quantity__button')) setTimeout(refrescar, 400);
    });
  });

  let pendiente = null;
  const pintarPendiente = () => {
    clearTimeout(pendiente);
    pendiente = setTimeout(refrescar, 30);
  };
})();
